// AsttHomeRosterTable.js
// ASTT-7353: "Roster(s) Assigned to [User]" table for the ASTT homepage.
// Follows the mockup directly: title + a count badge beside it (no separate
// stat strip), Project/Site filters, and status-color accents reusing this
// app's existing statusColorMap language.
//
// Grid: uses the shared PrimeReactDataGrid wrapper (same as
// TraineeDetailsGrid.js) rather than raw primereact DataTable/Column -
// this app's theme/font/sticky-header styling all lives under the
// #primeReactStyles wrapper that only PrimeReactDataGrid renders.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { format } from "date-fns";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { getAsttHomeAssignedRosters } from "services/userServices";
import {
  statusColorMap,
  SubHeading,
} from "components/SearchClassRoster/searchRostersConstants";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import BackdropComponent from "components/Spinner/Spinner";
import classes from "./AsttHomeRosterTable.module.css";

const formatDate = (value) =>
  value ? format(new Date(value), "dd-MMM-yyyy") : "NA";

const formatDateTime = (value) =>
  value ? format(new Date(value), "dd-MMM-yyyy hh:mm:ss a") : "NA";

// A roster's ProjectName can be a comma-separated aggregate (multiple
// projects on one class request) - same shape as searchClassRosterDetails.
// Filtering matches if the selected project appears anywhere in that string.
const splitProjectNames = (projectName) =>
  (projectName || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

export default function AsttHomeRosterTable() {
  const { t } = useTranslation(["AsttHome"], { useSuspense: false });
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rosters, setRosters] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);

  const userName = localStorage.getItem("User") || "";

  useEffect(() => {
    let cancelled = false;

    const fetchAssignedRosters = async () => {
      const oracleId = localStorage.getItem("OracleId");
      if (!oracleId) {
        // No Oracle Id resolved yet (e.g. still loading on Welcome) - nothing
        // to fetch. Leaves the table in its empty state rather than erroring.
        if (!cancelled) {
          setHasLoaded(true);
        }
        return;
      }

      try {
        if (!cancelled) {
          setLoading(true);
        }
        const response = await getAsttHomeAssignedRosters(oracleId);
        if (cancelled) return;

        if (response?.message === "Success" && response?.data) {
          setRosters(response.data);
        } else {
          // Per the updated SP, this now only happens when there are
          // literally zero Active rosters anywhere in the system - the
          // SP itself falls back to a system-wide list when the user has
          // no personal assignment, so an empty result here means "no
          // active rosters at all", not "nothing assigned to this user".
          setRosters([]);
        }
      } catch {
        if (!cancelled) {
          setRosters([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setHasLoaded(true);
        }
      }
    };

    fetchAssignedRosters();

    return () => {
      cancelled = true;
    };
  }, []);

  // The SP returns HasAssignment=true on every row when these are the user's
  // own assigned rosters, or HasAssignment=false when it fell back to the
  // system-wide "Recently Updated Class Rosters" list (AC section 4).
  // Dapper maps SQL BIT -> C# bool, which serializes as a JSON boolean
  // (true/false), not 0/1 - a strict "=== 0" check never matches that.
  const isFallbackView = rosters.length > 0 && !rosters[0]?.HasAssignment;

  const projectOptions = useMemo(() => {
    const allProjects = rosters.flatMap((roster) =>
      splitProjectNames(roster.ProjectName),
    );
    return [...new Set(allProjects)].sort();
  }, [rosters]);

  const siteOptions = useMemo(() => {
    const allSites = rosters.map((roster) => roster.SiteName).filter(Boolean);
    return [...new Set(allSites)].sort();
  }, [rosters]);

  const filteredRosters = useMemo(() => {
    return rosters.filter((roster) => {
      const matchesProject =
        !selectedProject ||
        splitProjectNames(roster.ProjectName).includes(selectedProject);
      const matchesSite = !selectedSite || roster.SiteName === selectedSite;
      return matchesProject && matchesSite;
    });
  }, [rosters, selectedProject, selectedSite]);

  const handleClearFilters = () => {
    setSelectedProject(null);
    setSelectedSite(null);
  };

  // Clicking the Request ID takes you to the request-level view (classRequestId
  // only); clicking the Roster ID takes you straight to that specific roster
  // (both params, so the page can pin the exact roster within the request).
  // Same-tab navigation - the app uses session storage, so a new tab would
  // hit an unauthenticated session and force a re-login.
  const handleNavigateToRequest = (rowData) => (event) => {
    event.stopPropagation();
    navigate(`/home/searchrosters?classRequestId=${rowData.ClassRequestId}`);
  };

  const handleNavigateToRoster = (rowData) => (event) => {
    event.stopPropagation();
    navigate(
      `/home/searchrosters?classRequestId=${rowData.ClassRequestId}&classRosterId=${rowData.ClassRosterId}`,
    );
  };

  // Project/Site text can run long (multi-project aggregates especially),
  // so both are truncated with an ellipsis and shown in full via tooltip -
  // same pattern RosterDetailCard/RosterSearch use elsewhere in this app -
  // instead of letting the column stretch and squeeze everything else out.
  const truncatedBody = (field) => (rowData) => (
    <Tooltip title={rowData[field] || ""} placement="bottom-start">
      <Typography variant="body2" className={classes.truncatedCell}>
        {rowData[field]}
      </Typography>
    </Tooltip>
  );

  const columns = [
    {
      field: "ClassRosterId",
      header: t("rosterTable.col_roster_id"),
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "right" },
      style: { minWidth: 110 },
      body: (rowData) => (
        <Typography
          variant="body2"
          className={`${classes.linkCell} ${classes.boldCell}`}
          onClick={handleNavigateToRoster(rowData)}
        >
          {rowData.ClassRosterId}
        </Typography>
      ),
    },
    {
      field: "ClassRequestId",
      header: t("rosterTable.col_request_id"),
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "right" },
      style: { minWidth: 110 },
      body: (rowData) => (
        <Typography
          variant="body2"
          className={`${classes.linkCell} ${classes.boldCell}`}
          onClick={handleNavigateToRequest(rowData)}
        >
          {rowData.ClassRequestId}
        </Typography>
      ),
    },
    {
      field: "Wave",
      header: t("rosterTable.col_wave_number"),
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "right" },
      style: { minWidth: 130 },
    },
    {
      field: "DataNitivClassReference",
      header: t("rosterTable.col_dn"),
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "right" },
      style: { minWidth: 100 },
    },
    {
      field: "RosterStatus",
      header: t("rosterTable.col_status"),
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "center" },
      style: { minWidth: 110 },
      body: (rowData) => {
        const statusColor = statusColorMap[rowData.RosterStatus] || "#3F5765";
        return (
          <Chip
            label={rowData.RosterStatus}
            size="small"
            sx={{
              backgroundColor: alpha(statusColor, 0.12),
              color: statusColor,
              fontWeight: 600,
              height: 22,
            }}
          />
        );
      },
    },
    {
      field: "ProjectName",
      header: t("rosterTable.col_project"),
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "left", maxWidth: 240 },
      style: { minWidth: 240 },
      body: truncatedBody("ProjectName"),
    },
    {
      field: "SiteName",
      header: t("rosterTable.col_site"),
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "left", maxWidth: 200 },
      style: { minWidth: 200 },
      body: truncatedBody("SiteName"),
    },
    {
      field: "RosterLockDate",
      header: t("rosterTable.col_planned_lock_date"),
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "center" },
      style: { minWidth: 160 },
      body: (rowData) => formatDate(rowData.RosterLockDate),
    },
    {
      field: "AssignTraineesLockTimeStamp",
      header: t("rosterTable.col_actual_lock_date_time"),
      sortable: true,
      align: "center",
      bodyStyle: { textAlign: "center" },
      style: { minWidth: 190 },
      body: (rowData) => formatDateTime(rowData.AssignTraineesLockTimeStamp),
    },
  ];

  if (!hasLoaded && loading) {
    return <BackdropComponent />;
  }

  if (hasLoaded && rosters.length === 0) {
    // With the SP's system-wide fallback in place, this only fires when
    // there are truly zero Active class rosters anywhere - not just "none
    // assigned to this user" (that case now shows the fallback view instead).
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" sx={{ color: "#3F5765", fontWeight: "bold" }}>
          {t("rosterTable.no_active_rosters_title")}
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b7c88", mt: 1 }}>
          {t("rosterTable.no_active_rosters_subtitle")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {loading ? <BackdropComponent /> : null}

      <Card
        sx={{
          mb: 2,
          borderRadius: "12px",
          boxShadow: "0 6px 20px rgba(15, 23, 42, 0.08)",
          overflow: "hidden",
        }}
      >
        <Box className={classes.titleBar}>
          <Typography className={classes.tableTitle} variant="h6">
            {isFallbackView
              ? t("rosterTable.fallback_title")
              : t("rosterTable.title", { name: userName })}
          </Typography>
          <Chip
            label={rosters.length}
            size="small"
            className={classes.countChip}
          />
        </Box>

        <Box className={classes.filterRow}>
          <Box className={classes.filterField}>
            <SubHeading>{t("rosterTable.filter_project")}</SubHeading>
            <Autocomplete
              disablePortal
              size="small"
              options={projectOptions}
              value={selectedProject}
              onChange={(event, newValue) => setSelectedProject(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("rosterTable.filter_all_projects")}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Box>
          <Box className={classes.filterField}>
            <SubHeading>{t("rosterTable.filter_site")}</SubHeading>
            <Autocomplete
              disablePortal
              size="small"
              options={siteOptions}
              value={selectedSite}
              onChange={(event, newValue) => setSelectedSite(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("rosterTable.filter_all_sites")}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Box>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleClearFilters}
            sx={{ mb: 0.5 }}
          >
            {t("rosterTable.clear_filters")}
          </Button>
        </Box>

        <PrimeReactDataGrid
          dataKey="ClassRosterId"
          value={filteredRosters}
          columns={columns}
          filterprops={{}}
          enableGlobalFilter={false}
          enableScrollable={true}
          enableStickyHeader={true}
          scrollHeight="calc(100vh - 390px)"
          resizableColumns
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate={t("rosterTable.pagination_template")}
          columnResizeMode="expand"
          sortMode="multiple"
          emptyMessage={t("rosterTable.no_matching_filters")}
        />
      </Card>
    </Box>
  );
}
